import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useDemoSafeMutation } from "@/hooks/useDemoGuard";
import { employeesRepository } from "@/repositories/employees";
import { useAuth } from "@/hooks/useAuth";
import { useEmployeeRecord } from "@/hooks/useEmployeeData";
import { useEmployeePropertyAssignments } from "@/hooks/useEmployeePropertyAssignments";
import { usePermissions } from "@/hooks/usePermissions";
import { toast } from "sonner";
import { useLanguage } from "@/contexts/LanguageContext";
import { serializeError } from "@/lib/sentry";
import { isValidUUID } from "@/lib/uuid";
import { propertiesRepository, type OwnerUnitInput } from "@/repositories/properties";
import type { Database } from "@/integrations/supabase/types";
import type { RerSnapshot } from "@/lib/rer-property-builder";

type PropertyRow = Database["public"]["Tables"]["properties"]["Row"];
type PropertyInsert = Database["public"]["Tables"]["properties"]["Insert"];
type PropertyUpdate = Database["public"]["Tables"]["properties"]["Update"];

export type Property = PropertyRow;
export type PropertyWithUnits = Property & { units: { images: unknown }[] };

export interface CreatePropertyData {
  name: string;
  name_en?: string;
  purpose?: string;
  property_type?: string;
  address?: string;
  city?: string;
  district?: string;
  postal_code?: string;
  total_area?: number;
  built_year?: number;
  floors_count?: number;
  description?: string | null;
  description_en?: string | null;
  /** Public property page "Available Units" source: 'listings' (default) or 'vacant_units'. */
  public_units_source?: "listings" | "vacant_units";
  deed_number?: string;
  /** REGA Real Estate Registry number (الرقم العقاري) — matched with deed_number on brokerage import. */
  rer_number?: string | null;
  // Optional compound (gated complex) metadata — used by the field
  // employee's task context card to know which compound to enter and the
  // gate code.
  compound_name?: string;
  compound_code?: string;
  owner_id?: string;
  latitude?: number;
  longitude?: number;
  images?: string[];
  amenities?: {
    utilities?: string[];
    highlights?: Record<string, string[]>;
    // Full RER (Real Estate Registry) title snapshot, persisted on RER import so
    // no field the registry returns is dropped (owners, borders, per-unit
    // details, codes, dual dates …). See `RerSnapshot`.
    rer?: RerSnapshot;
    legal?: {
      ownershipType?: string;
      ownershipNumber?: string;
      ownershipDate?: string;
      infoRestriction?: string | null;
      mortgageRestriction?: string | null;
      obligations?: string | null;
      propertyDispute?: string | null;
      ownershipDocuments?: string[];
    };
  };
}

export function useProperties() {
  const { companyId, activeRole, user } = useAuth();
  const { hasPermission } = usePermissions();
  const isEmployee = activeRole === "employee";
  const { data: employeeRecord, isLoading: employeeLoading } = useEmployeeRecord();
  const { data: assignments, isLoading: assignmentsLoading } = useEmployeePropertyAssignments(
    isEmployee ? employeeRecord?.id : undefined
  );

  // Layer 1 — permission gate (universal). No view permission → empty list.
  // Admin/super_admin pass via BYPASS_ROLES inside hasPermission.
  const canViewProperties = hasPermission("properties.view");

  // Layer 2 — for employees, always scope by their property assignments,
  // even when they hold properties.view. null = not-an-employee (no scope filter).
  const employeeDataReady = isEmployee && !employeeLoading && !assignmentsLoading;
  const assignedIds = isEmployee
    ? (employeeDataReady ? (assignments ?? []).map((a) => a.property_id) : null)
    : null;

  return useQuery({
    queryKey: ["properties", companyId, isEmployee ? assignedIds : "all"],
    queryFn: async () => {
      if (!companyId) return [];
      if (!canViewProperties) return [];
      if (isEmployee && assignedIds !== null && assignedIds.length === 0) return [];

      try {
        return await propertiesRepository.list({
          companyId,
          assignedIds: isEmployee ? assignedIds : null,
        });
      } catch (error) {
        console.error("Error fetching properties:", serializeError(error));
        throw new Error((error as { message?: string })?.message);
      }
    },
    enabled: !!companyId && (!isEmployee || (!!employeeRecord && !assignmentsLoading)),
  });
}

export function useProperty(id: string | undefined) {
  const { companyId } = useAuth();

  return useQuery({
    queryKey: ["property", id],
    queryFn: async () => {
      if (!id || !companyId) return null;

      try {
        return await propertiesRepository.getById(id, companyId);
      } catch (error) {
        console.error("Error fetching property:", serializeError(error));
        throw new Error((error as { message?: string })?.message);
      }
    },
    enabled: !!id && !!companyId && isValidUUID(id),
  });
}

export function useCreateProperty() {
  const queryClient = useQueryClient();
  const { companyId, activeRole, user } = useAuth();
  const { data: employeeRecord } = useEmployeeRecord();
  const { t } = useLanguage();

  return useDemoSafeMutation({
    mutationFn: async (propertyData: CreatePropertyData) => {
      if (!companyId) throw new Error("No company ID");

      const insertData = {
        ...propertyData,
        company_id: companyId,
        property_type: propertyData.property_type || "residential",
      } as PropertyInsert;

      const data = await propertiesRepository.create(insertData).catch((error) => {
        console.error("Error creating property:", serializeError(error));
        throw new Error((error as { message?: string })?.message);
      });

      // Auto-assign the new property to the creating employee so they keep
      // access to their own creation under the always-on assignment scope.
      // Admin/owner/manager creators don't need this — they're not scoped by
      // assignments. Repointed onto the existing employees assignments slice
      // (Nest-first, identical upsert fallback).
      if (activeRole === "employee" && employeeRecord?.id) {
        try {
          await employeesRepository.assignProperties(
            employeeRecord.id,
            companyId,
            [data.id],
            user?.id ?? null,
          );
        } catch (assignError) {
          // Don't fail the create — log and surface a warning. The property
          // exists; an admin can manually assign it if the auto-assign failed.
          console.error("Auto-assignment failed for new property:", serializeError(assignError));
        }
      }

      // Fire-and-forget AR↔EN auto-translation. Soft-fails inside the
      // repository — the property has already saved either way. Awaited so
      // the listings list refetch in onSuccess sees the translated copies
      // on the very first render.
      if (data && typeof (data as { id?: unknown }).id === "string") {
        await propertiesRepository.translate((data as { id: string }).id);
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["properties"] });
      queryClient.invalidateQueries({ queryKey: ["units-stats"] });
      queryClient.invalidateQueries({ queryKey: ["employee-property-assignments"] });
      toast.success(t("تم إضافة العقار بنجاح", "Property added successfully"));
    },
    onError: (error) => {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error("Create property error:", errorMessage);

      if (error instanceof Error && error.message.includes("No company ID")) {
        toast.error(
          t(
            "لا يمكن إضافة عقار لأن حسابك غير مرتبط بشركة. جرّب تسجيل الخروج ثم الدخول مرة أخرى.",
            "Can't add a property because your account isn't linked to a company yet. Try signing out and back in."
          )
        );
        return;
      }

      toast.error(t("حدث خطأ أثناء إضافة العقار", "Error adding property"));
    },
  });
}

/**
 * Owner adds a property DIRECTLY (Pkg 1) — via the owner-callable RPC (RLS blocks
 * a direct owner insert). Live immediately, no admin approval (CEO 2026-07).
 */
export function useOwnerCreateProperty() {
  const queryClient = useQueryClient();
  const { companyId } = useAuth();
  const { t } = useLanguage();

  return useDemoSafeMutation({
    mutationFn: async (params: {
      name: string;
      nameEn?: string | null;
      purpose?: string | null;
      propertyType?: string;
      totalArea?: number | null;
      address?: string | null;
      city?: string | null;
      district?: string | null;
      images?: string[];
      documents?: string[];
      units?: OwnerUnitInput[];
    }) => {
      if (!companyId) throw new Error("No company ID");
      return propertiesRepository.ownerCreateProperty({ companyId, ...params });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["owner-properties"] });
      queryClient.invalidateQueries({ queryKey: ["properties"] });
      toast.success(t("تمت إضافة العقار", "Property added"));
    },
    onError: () => {
      toast.error(t("حدث خطأ أثناء إضافة العقار", "Error adding property"));
    },
  });
}

export function useUpdateProperty() {
  const queryClient = useQueryClient();
  const { companyId } = useAuth();
  const { t } = useLanguage();

  return useDemoSafeMutation({
    mutationFn: async ({ id, ...rest }: { id: string } & Partial<CreatePropertyData>) => {
      if (!companyId) throw new Error("No company ID");
      
      const updateData = {
        ...rest,
      } as PropertyUpdate;
      
      const data = await propertiesRepository.update(id, updateData, companyId).catch((error) => {
        console.error("Error updating property:", serializeError(error));
        throw new Error((error as { message?: string })?.message);
      });

      if (!data) {
        throw new Error("Property not found or access denied");
      }

      // Re-translate ONLY when free-text fields actually changed. Price /
      // images / FK edits don't need the Claude round-trip.
      const textChanged = (
        typeof rest.name === "string" ||
        typeof rest.address === "string" ||
        typeof rest.description === "string" ||
        typeof (rest as { compound_name?: unknown }).compound_name === "string" ||
        typeof (rest as { city?: unknown }).city === "string" ||
        typeof (rest as { district?: unknown }).district === "string"
      );
      if (textChanged) {
        await propertiesRepository.translate(id);
      }

      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["properties"] });
      queryClient.invalidateQueries({ queryKey: ["property", data.id] });
      toast.success(t("تم تحديث العقار بنجاح", "Property updated successfully"));
    },
    onError: (error) => {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error("Update property error:", errorMessage);
      toast.error(t("حدث خطأ أثناء تحديث العقار", "Error updating property"));
    },
  });
}

export function useDeleteProperty() {
  const queryClient = useQueryClient();
  const { companyId } = useAuth();
  const { t } = useLanguage();

  return useDemoSafeMutation({
    mutationFn: async (id: string) => {
      if (!companyId) throw new Error("No company ID");
      
      await propertiesRepository.softDelete(id, companyId).catch((error) => {
        console.error("Error deleting property:", serializeError(error));
        throw new Error((error as { message?: string })?.message);
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["properties"] });
      queryClient.invalidateQueries({ queryKey: ["units-stats"] });
      toast.success(t("تم حذف العقار بنجاح", "Property deleted successfully"));
    },
    onError: (error) => {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error("Delete property error:", errorMessage);
      toast.error(t("حدث خطأ أثناء حذف العقار", "Error deleting property"));
    },
  });
}

// Stats hook for properties by type — V5: industrial and land shown separately
// Aligned with useProperties(): excludes soft-deleted + respects employee assignments
// Two-layer access model: properties.view (Layer 1 gate) + employee assignment scope (Layer 2).
// Employees are always scoped to assigned properties; permission only gates whether the query runs.
/** Optional month/year (or custom) date bounds — ISO strings, "all time" = both undefined. */
export interface StatsDateRange {
  startDate?: string;
  endDate?: string;
}

export function usePropertiesStats(range?: StatsDateRange) {
  const { companyId, activeRole } = useAuth();
  const { hasPermission } = usePermissions();
  const isEmployee = activeRole === "employee";
  const { data: employeeRecord, isLoading: employeeLoading } = useEmployeeRecord();
  const { data: assignments, isLoading: assignmentsLoading } = useEmployeePropertyAssignments(
    isEmployee ? employeeRecord?.id : undefined
  );

  const canViewProperties = hasPermission("properties.view");

  const employeeDataReady = isEmployee && !employeeLoading && !assignmentsLoading;
  const assignedIds = isEmployee
    ? (employeeDataReady ? (assignments ?? []).map((a) => a.property_id) : null)
    : null;

  const emptyStats = { total: 0, residential: 0, commercial: 0, industrial: 0, land: 0, mixed: 0 };

  return useQuery({
    queryKey: ["properties-stats", companyId, isEmployee ? assignedIds : "all", range?.startDate, range?.endDate],
    queryFn: async () => {
      if (!companyId) return emptyStats;
      if (!canViewProperties) return emptyStats;
      if (isEmployee && assignedIds !== null && assignedIds.length === 0) return emptyStats;

      try {
        return await propertiesRepository.statsByPurpose({
          companyId,
          assignedIds: isEmployee ? assignedIds : null,
          startDate: range?.startDate,
          endDate: range?.endDate,
        });
      } catch (error) {
        throw new Error((error as { message?: string })?.message);
      }
    },
    enabled: !!companyId && (!isEmployee || (!!employeeRecord && !assignmentsLoading)),
  });
}
